import React, { forwardRef, useEffect, useRef, useState } from 'react';
import styled from "styled-components";
import { Image, Layer, Stage } from 'react-konva';
import useImage from 'use-image';

import PlayButton from "../../images/activity-default/btn_song_play.png";
import { useAudio } from '../../shared/hooks';

// 스타일 컴포넌트
const BoxInner = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
`;

// 컴포넌트
const MiniSitcomKeyExpression = forwardRef((props, ref) => {
    const { categorySelected, mediaPath, activityProps, rightButtonRef, playButtonRef} = props;

    const { play } = useAudio()

    const imgRef = useRef();
    const stageRef = useRef();
    const containerRef = useRef();

    const [counter, setCounter] = useState(0);
    const [isPlaying, setIsPlaying] = useState(false)
    const [isShownFirstPlayBtn, setIsShownFirstPlayBtn] = useState(true)
    const [isShownNextPlayBtn, setIsShownNextPlayBtn] = useState(true)
    const [isShownControllerBtn, setIsShownControllerBtn] = useState(false)

    const length = categorySelected.keyaudio.length;

    // 스테이지 크기 설정
    useEffect(() => {
        const resizeStage = () => {
            let containerWidth = containerRef.current.offsetWidth;
            let containerHeight = containerRef.current.offsetHeight;
            let scaleX = containerWidth / activityProps.defaultWidth;
            let scaleY = containerHeight / activityProps.defaultHeight;
            let scale = Math.min(scaleX, scaleY);

            if (scaleX > scaleY) {
                containerWidth = activityProps.defaultWidth * scaleY;
            } else {
                containerHeight = activityProps.defaultHeight * scaleX;
            }

            stageRef.current
            .width(containerWidth)
            .height(containerHeight)
            .scale({ x: scale, y: scale })
            .batchDraw();
        };

        resizeStage();
        window.addEventListener('resize', resizeStage);
        return () => window.removeEventListener('resize', resizeStage);
    }, []);

    useEffect(() => {
        // 마지막 오디오 재생 마친 후 Next 버튼 숨기기
        if (counter === length - 1) {
            setIsShownNextPlayBtn(false)
        }
    }, [counter])

    // 이미지 로드 컴포넌트
    const LoadImage = (props) => {
        const [image, status] = useImage(props.imagePath);
        if (status !== "loaded") return null;

        const imageWidth = props.width || image.width;
        const imageHeight = props.height || image.height;
        let x = props.x ?? (props.center ? (activityProps.defaultWidth - imageWidth) / 2 : 0);
        let y = props.y ?? (props.center ? (activityProps.defaultHeight - imageHeight) / 2 : 0);

        return (
          <Image
            image={image}
            x={x}
            y={y}
            width={props.width}
            height={props.height}
          />
        );
    };

    // ==================================================================================================

    // audio source url 생성
    // const handleCreateAudioSourceUrl = (index) => {
    //     return mediaPath + categorySelected.keyaudio[index] + '?v=' + Math.random()
    // }

    const handleCreateAudioSourceUrl = (index) => {
        return 'https://pub-f6f03c5a968340578d16884b642a4c78.r2.dev/' + categorySelected.keyaudio[index]
    }

    const handlePlayAudio = (index) => {
        setIsShownFirstPlayBtn(false)

        if (isPlaying) return
        setIsShownControllerBtn(false)

        const audioSrc = handleCreateAudioSourceUrl(index)
        play(audioSrc, () => {
            setIsPlaying(false)
            setIsShownControllerBtn(true)
        })

        // const audio = new Audio(audioSrc);
        // audio.play().then(() => setIsPlaying(true))
        // audio.onended = () => {
        //     setIsPlaying(false)
        //     setIsShownControllerBtn(true)
        // }
    }

    // Next 버튼 클릭
    const handleClickNextBtn = () => {
        const nextIndex = counter + 1
        setCounter(nextIndex)
        handlePlayNextStep(nextIndex)
    }

    // image source 업데이트
    const handleChangeImgaeSrc = (index) => {
        imgRef.current.src = mediaPath + categorySelected.keyimg[index];
    }

    const handlePlayNextStep = (index) => {
        // const nextIndex = counter + 1;

        // 마지막 오디오인지 확인합니다.
        if (index < length) {
            setCounter(index)
            handleChangeImgaeSrc(index) // 이미지 소스 업데이트
            handlePlayAudio(index) // 오디오 플레이
        }

        // 마지막 오디오라면 다음 버튼을 표시합니다.
        if (index === length - 1) {
            if (rightButtonRef.current) {
                rightButtonRef.current.style.display = 'block';
                rightButtonRef.current.style.visibility = 'visible';
            }

            if (playButtonRef.current) {
                playButtonRef.current.style.display = 'none';
            }
        }
    }

    return (
      <BoxInner ref={containerRef}>
          <div className="key_expression_wrapper">
              <div className="titleA">Key Expression <span>{counter + 1}</span></div>
              <div className="key_box" style={{ display: 'flex', flexDirection: 'column', alignItems: 'space-between' }}>
                  <img className='key_image' ref={imgRef} src={mediaPath + categorySelected.keyimg[0]} alt="" />

                  {isShownFirstPlayBtn && (
                    <div className='first_play_btn' onClick={() => handlePlayAudio(0)}>
                        <img src={PlayButton} alt="첫 재생 버튼" />
                    </div>
                  )}
              </div>
              <div className='key_button_box' style={{ display: 'flex', justifyContent: 'center', gap: '0.75rem', marginTop: 'auto' }}>
                  <button
                    className='key_button'
                    style={{ backgroundColor: isShownControllerBtn ? '#449ce4' : 'rgba(0, 0, 0, 0.15)', cursor: isShownControllerBtn ? 'pointer' : 'default' }}
                    onClick={() => isShownControllerBtn && handlePlayAudio(counter)}
                  >Try Again</button>
                  {isShownNextPlayBtn &&
                    <button
                      className='key_button'
                      style={{ backgroundColor: isShownControllerBtn ? 'rgba(38,61,116)' : 'rgba(0, 0, 0, 0.15)', cursor: isShownControllerBtn ? 'pointer' : 'default' }}
                      onClick={isShownControllerBtn && handleClickNextBtn}
                    >Next</button>
                  }
              </div>
          </div>

          <Stage
            style={{ display: 'none' }}
            width={activityProps.defaultWidth}
            height={activityProps.defaultHeight}
            ref={stageRef}
          >
              <Layer>
                  <LoadImage imagePath={mediaPath + categorySelected.image} center={true} />
              </Layer>
          </Stage>
      </BoxInner>
    );
});

export default MiniSitcomKeyExpression;